import { useMemo } from 'react'
import { parseLocalDate, toLocalYmd, toMonthKey } from '../utils'
import { computeRevisedSPFromItem, sumBucketChargesForGraph } from '../utils/dashboardMath'
import { mapQboLineTypeToChargeLabel, dedupeQboChargeRows } from '../utils/qboChargeMapping'

const useDashboardData = ({
  billingInfo,
  trackerInfo,
  ticketsInfo,
  organizationTickets,
  vulnerabilityTickets,
  upgradeTickets,
  qboByCustomer,
  selectedCharges,
  excludedChargeRowKeys,
  constants,
  graphFilters,
  clientSettings = {},
  clientOptions = {},
  projectAvailabilityOverrides = null
}) => {
  const { catNames } = constants

  return useMemo(() => {
    if (
      !billingInfo || typeof billingInfo !== 'object' ||
      !trackerInfo || typeof trackerInfo !== 'object' ||
      !ticketsInfo || typeof ticketsInfo !== 'object' ||
      !organizationTickets || typeof organizationTickets !== 'object'
    ) return null

    const hasEntries = (arr) => Array.isArray(arr) && arr.length > 0

    const projectKeys = Object.values(clientOptions || {}).filter((v) => (
      typeof v === 'string' &&
      v.trim() &&
      typeof clientSettings?.[v] === 'object' &&
      Boolean(clientSettings?.[v])
    ))
    if (!projectKeys.length) return null

    const monthKey = (d) => toMonthKey(d)

    const buildMonthRange = (startDate, endDate) => {
      const months = []
      let cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1)

      while (cur <= end) {
        const start = new Date(cur.getFullYear(), cur.getMonth(), 1)
        const endM = new Date(cur.getFullYear(), cur.getMonth() + 1, 0)
        months.push({ key: monthKey(cur), start, end: endM })
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1)
      }

      return months
    }

    const clampDate = (d, min, max) =>
      new Date(Math.min(max.getTime(), Math.max(min.getTime(), d.getTime())))

    const dayMillis = 86400000
    const daysInclusive = (a, b) => Math.floor((b - a) / dayMillis) + 1

    const allocateAcrossMonths = (months, start, end, value) => {
      if (!(start instanceof Date) || !(end instanceof Date)) return {}
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return {}
      if (end < start) return {}

      const totalDays = daysInclusive(start, end)
      if (totalDays <= 0) return {}

      return months.reduce((acc, m) => {
        if (end < m.start || start > m.end) return acc

        const ovStart = clampDate(start, m.start, m.end)
        const ovEnd = clampDate(end, m.start, m.end)
        const ovDays = daysInclusive(ovStart, ovEnd)
        const ratio = ovDays / totalDays

        acc[m.key] = (acc[m.key] || 0) + (value * ratio)
        return acc
      }, {})
    }

    const addNum = (acc, mKey, field, val) => {
      if (!acc[mKey]) return acc
      acc[mKey][field] = (acc[mKey][field] || 0) + val
      return acc
    }

    const addToArray = (acc, mKey, field, item) => {
      if (!acc[mKey]) return acc
      acc[mKey][field].push(item)
      return acc
    }

    const normalizeProjectKeys = (n8nTrackerSet) => {
      const src = Array.isArray(n8nTrackerSet) ? n8nTrackerSet : []

      return src.reduce((arr, v) => {
        if (!v) return arr
        if (typeof v === 'string') return arr.concat(v)
        if (typeof v === 'object' && typeof v.jiraProjectKey === 'string') return arr.concat(v.jiraProjectKey)
        if (typeof v === 'object' && typeof v.key === 'string') return arr.concat(v.key)
        return arr
      }, [])
    }

    const normalize = (s) => {
      if (typeof s !== 'string') return ''
      const t = s.trim().toLowerCase()
      return t.replace(/\u2013|\u2014/g, '-').replace(/\s+/g, ' ')
    }
    const jiraTicketKeys = (obj) =>
      Object.keys(obj || {}).filter((k) => k !== 'orphans' && typeof k === 'string' && k.trim())
    const allJiraApiKeys = [
      ...jiraTicketKeys(organizationTickets),
      ...jiraTicketKeys(vulnerabilityTickets),
      ...jiraTicketKeys(upgradeTickets)
    ]
    const resolveJiraKeysForLookup = (allowedNames) => {
      const allowed = (allowedNames || []).map(normalize).filter(Boolean)
      if (!allowed.length) return []
      const uniqueKeys = [...new Set(allJiraApiKeys)]
      return uniqueKeys.filter((apiKey) => {
        const keyNorm = normalize(apiKey)
        if (!keyNorm) return false
        if (allowed.includes(keyNorm)) return true
        return allowed.some((a) => keyNorm.includes(a) || a.includes(keyNorm))
      })
    }
    const keysWithTicketsForLookup = (allowedNames) => {
      const resolved = resolveJiraKeysForLookup(allowedNames)
      const direct = (allowedNames || []).filter(
        (name) =>
          Array.isArray(organizationTickets?.[name]) ||
          Array.isArray(vulnerabilityTickets?.[name]) ||
          Array.isArray(upgradeTickets?.[name])
      )
      return [...new Set([...resolved, ...direct])]
    }
    const getJiraMapForProjectKey = (projectKey) => {
      if (!projectKey) return []
      const direct = clientSettings?.[projectKey]?.jiraProjects
      if (Array.isArray(direct) && direct.length) return direct
      const projectKeyNorm = normalize(projectKey)
      if (!projectKeyNorm) return []
      const keys = Object.keys(clientSettings || {}).filter((k) => k && k !== 'undefined')
      const exact = keys.find((k) => normalize(k) === projectKeyNorm)
      if (exact) return clientSettings[exact]?.jiraProjects || []
      const prefixMatch = keys.filter(
        (k) => normalize(k).startsWith(projectKeyNorm) || projectKeyNorm.startsWith(normalize(k))
      )
      const best = prefixMatch.length === 1
        ? prefixMatch[0]
        : prefixMatch.sort((a, b) => normalize(b).length - normalize(a).length)[0]
      const arr = clientSettings?.[best]?.jiraProjects
      return Array.isArray(arr) ? arr : []
    }

    const getGoogleMapForProjectKey = (projectKey) => {
      if (!projectKey) return []
      const direct = clientSettings?.[projectKey]?.googleProjects
      if (Array.isArray(direct) && direct.length) return direct
      return []
    }

    const getQboMapForProjectKey = (projectKey) => {
      if (!projectKey) return []
      const direct = clientSettings?.[projectKey]?.qboMappings
      return Array.isArray(direct) ? direct : []
    }

    const getChargesForProjectKey = (projectKey) => {
      if (!projectKey) return []
      const direct = clientSettings?.[projectKey]?.charges
      return Array.isArray(direct) ? direct : []
    }

    const flattenSheetSetsForClient = (clientKey) => {
      const sheetKeys = getGoogleMapForProjectKey(clientKey)
      const billingSet = sheetKeys.flatMap((k) => (Array.isArray(billingInfo?.[k]) ? billingInfo[k] : []))
      const trackerSet = sheetKeys.flatMap((k) => (Array.isArray(trackerInfo?.[k]) ? trackerInfo[k] : []))
      const ticketsSet = sheetKeys.flatMap((k) => (Array.isArray(ticketsInfo?.[k]) ? ticketsInfo[k] : []))
      return { billingSet, trackerSet, ticketsSet }
    }

    // Client charges list + mapping only; checkboxes only affect the graph (sumBucketChargesForGraph).
    const flattenQboForClient = (clientKey) => {
      const qboKeys = getQboMapForProjectKey(clientKey)
      const allowedCharges = getChargesForProjectKey(clientKey)
      const normalizedAllowedCharges = allowedCharges.map((c) => normalize(c)).filter(Boolean)

      const rows = qboKeys.flatMap((k) => (Array.isArray(qboByCustomer?.[k]) ? qboByCustomer[k] : []))

      const rowsWithChargeLabel = rows.map((qboRow) => {
        const lineType = qboRow?.qboInvoiceItemLineType || qboRow?.lineType || ''
        const lineDesc = qboRow?.qboInvoiceItemLineDesc || qboRow?.chargeName || ''
        const chargeLabel = mapQboLineTypeToChargeLabel(lineType, lineDesc)
        return { qboRow, chargeLabel }
      })

      const isChargeAllowedForClient = ({ chargeLabel }) => {
        if (!chargeLabel) return false
        if (!normalizedAllowedCharges.length) return true
        if (allowedCharges.includes(chargeLabel)) return true
        const normalizedLabel = normalize(chargeLabel)
        return normalizedAllowedCharges.some((allowed) => allowed === normalizedLabel)
      }

      return rowsWithChargeLabel
        .filter(isChargeAllowedForClient)
        .map(({ qboRow }) => qboRow)
    }

    const computeTrackingValue = (item) => computeRevisedSPFromItem(item)

    const seedBaseBuckets = (months) => {
      return months.reduce((acc, m) => {
        acc[m.key] = {
          month: m.key,
          startDate: toLocalYmd(m.start),
          endDate: toLocalYmd(m.end),
          label: m.start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),

          Dollars: 0,
          SP: 0,
          TicketCount: 0,

          Tickets: [],
          Vulnerabilities: [],
          Upgrades: [],
          Charges: [],

          AiUnits: 0,
          AiPerItem: 0
        }
        return acc
      }, {})
    }

    const projectKeysReduce = projectKeys.reduce((obj, projectKey) => {
      if (typeof projectKey !== 'string' || !projectKey) return obj
      const { billingSet, trackerSet, ticketsSet } = flattenSheetSetsForClient(projectKey)
      const qboSet = flattenQboForClient(projectKey)

      const hasGoogleConfig = hasEntries(clientSettings?.[projectKey]?.googleProjects)
      const hasQboConfig = hasEntries(clientSettings?.[projectKey]?.qboMappings)

      const n8nTrackerSet = getJiraMapForProjectKey(projectKey)
      const allowedJiraNames = normalizeProjectKeys(n8nTrackerSet)
      const jiraProjectKeys = keysWithTicketsForLookup(allowedJiraNames)
      const availabilityOverride = projectAvailabilityOverrides?.[projectKey]
      const enableGoogle = availabilityOverride
        ? Boolean(availabilityOverride.google)
        : hasGoogleConfig
      const enableN8n = availabilityOverride
        ? Boolean(availabilityOverride.n8n)
        : hasQboConfig

      obj.projectAvailability[projectKey] = {
        google: enableGoogle,
        n8n: enableN8n
      }

      if (!enableGoogle && !enableN8n) return obj

      const borderColor = clientSettings?.[projectKey]?.color || `#${Math.floor(Math.random() * 16777215).toString(16)}`

      const rangeStart = parseLocalDate('2024-01-01') || new Date(2024, 0, 1)
      const rangeEnd = new Date()

      const months = buildMonthRange(rangeStart, rangeEnd)
      const base = seedBaseBuckets(months)

      const withBilling = billingSet.reduce((acc, projectBilling) => {
        if (!projectBilling || typeof projectBilling !== 'object') return acc

        const start = parseLocalDate(projectBilling.startDate)
        const end = parseLocalDate(projectBilling.endDate)
        const value = Number(projectBilling.value) || 0

        const alloc = allocateAcrossMonths(months, start, end, value)

        return Object.entries(alloc).reduce((a, [mKey, dollars]) => {
          return addNum(a, mKey, 'Dollars', Math.round(dollars))
        }, acc)
      }, base)

      const withTracker = trackerSet.reduce((acc, clientTracker) => {
        if (!clientTracker || typeof clientTracker !== 'object') return acc

        const start = parseLocalDate(clientTracker.startDate)
        const end = parseLocalDate(clientTracker.endDate)
        const value = Number(clientTracker.value) || 0

        const alloc = allocateAcrossMonths(months, start, end, value)

        return Object.entries(alloc).reduce((a, [mKey, sp]) => {
          return addNum(a, mKey, 'SP', Math.round(sp))
        }, acc)
      }, withBilling)

      const withTickets = ticketsSet.reduce((acc, t) => {
        if (!t || typeof t !== 'object') return acc
        const start = parseLocalDate(t.startDate)
        if (!start) return acc

        const key = monthKey(start)
        return addNum(acc, key, 'TicketCount', Number(t.value) || 0)
      }, withTracker)

      const withQbo = qboSet.reduce((acc, qboItem) => {
        if (!qboItem || typeof qboItem !== 'object') return acc

        const start = parseLocalDate(qboItem.startDate || qboItem.qboInvoiceDate)
        const end = parseLocalDate(qboItem.endDate || qboItem.qboInvoiceDate || qboItem.startDate)
        if (!start || !end) return acc

        const lineTypeRaw = qboItem.qboInvoiceItemLineType || qboItem.lineType || ''
        const lineDescRaw = qboItem.qboInvoiceItemLineDesc || qboItem.chargeName || ''
        const chargeCategory = mapQboLineTypeToChargeLabel(lineTypeRaw, lineDescRaw)

        const alloc = allocateAcrossMonths(months, start, end, Number(qboItem.value) || 0)
        return Object.entries(alloc).reduce((a, [mKey, dollars]) => {
          const rounded = Math.round(dollars)
          if (rounded === 0) return a
          const chargeRow = {
            clientName: projectKey,
            customerName: (qboItem.customerName || qboItem.qboCustomerName || '').trim(),
            chargeName: (qboItem.chargeName || qboItem.qboInvoiceItemLineDesc || '').trim(),
            lineType: (qboItem.lineType || qboItem.qboInvoiceItemLineType || '').trim(),
            invoiceDate: qboItem.startDate || qboItem.qboInvoiceDate || '',
            invoiceNumber: qboItem.qboInvoiceNumber || '',
            amount: rounded,
            billingMonthKey: mKey,
            chargeCategory: chargeCategory || ''
          }
          addToArray(a, mKey, 'Charges', chargeRow)
          return a
        }, acc)
      }, withTickets)

      const withJira = jiraProjectKeys.reduce((acc, jiraProjectKey) => {
        const supportList = organizationTickets?.[jiraProjectKey]
        const vulnList = vulnerabilityTickets?.[jiraProjectKey]
        const upgradeList = upgradeTickets?.[jiraProjectKey]

        const afterSupport = Array.isArray(supportList)
          ? supportList.reduce((a, ticketInfo) => {
            if (!ticketInfo || typeof ticketInfo !== 'object') return a

            const start = parseLocalDate(ticketInfo.startDate)
            const end = parseLocalDate(ticketInfo.endDate)
            if (!start || !end) return a

            const trackingValue = computeTrackingValue(ticketInfo)
            const alloc = allocateAcrossMonths(months, start, end, trackingValue)

            return Object.entries(alloc).reduce((b, [mKey, v]) => {
              const revisedSP = Math.ceil(v)
              addToArray(b, mKey, 'Tickets', { ...ticketInfo, revisedSP, jiraProjectKey, supportType: graphFilters.SUPPORT })
              return b
            }, a)
          }, acc)
          : acc

        // Vulns allocate SP across months like support (from time/SP fields), not a flat count
        const afterVulns = Array.isArray(vulnList)
          ? vulnList.reduce((a, vuln) => {
            if (!vuln || typeof vuln !== 'object') return a

            const start = parseLocalDate(vuln.startDate || vuln.createdDate)
            const end = parseLocalDate(vuln.endDate || vuln.resolvedDate || vuln.startDate || vuln.createdDate)
            if (!start || !end) return a

            const trackingValue = computeTrackingValue(vuln)
            const alloc = allocateAcrossMonths(months, start, end, trackingValue)

            return Object.entries(alloc).reduce((b, [mKey, v]) => {
              const revisedSP = Math.ceil(v)
              if (revisedSP > 0) addToArray(b, mKey, 'Vulnerabilities', { ...vuln, revisedSP, jiraProjectKey, supportType: graphFilters.VULNERABILITIES })
              return b
            }, a)
          }, afterSupport)
          : afterSupport

        const afterUpgrades = Array.isArray(upgradeList)
          ? upgradeList.reduce((a, upgrade) => {
            if (!upgrade || typeof upgrade !== 'object') return a

            const start = parseLocalDate(upgrade.startDate)
            const end = parseLocalDate(upgrade.endDate)
            if (!start || !end) return a

            const trackingValue = computeTrackingValue(upgrade)
            const alloc = allocateAcrossMonths(months, start, end, trackingValue)

            return Object.entries(alloc).reduce((b, [mKey, v]) => {
              const revisedSP = Math.ceil(v)
              addToArray(b, mKey, 'Upgrades', { ...upgrade, revisedSP, jiraProjectKey, supportType: graphFilters.UPGRADES })
              return b
            }, a)
          }, afterVulns)
          : afterVulns

        return afterUpgrades
      }, withQbo)

      Object.keys(withJira).forEach((mKey) => {
        const bucket = withJira[mKey]
        const charges = bucket?.Charges
        if (Array.isArray(charges) && charges.length) {
          bucket.Charges = dedupeQboChargeRows(charges)
        }
      })

      const projectKeyMonthlyData = Object.keys(withJira).reduce((acc, mKey) => {
        const bucket = acc[mKey]

        const items = [
          ...(Array.isArray(bucket.Tickets) ? bucket.Tickets : []),
          ...(Array.isArray(bucket.Upgrades) ? bucket.Upgrades : []),
          ...(Array.isArray(bucket.Vulnerabilities) ? bucket.Vulnerabilities : [])
        ]

        const units = items.reduce((sum, item) => {
          if (!item || typeof item !== 'object') return sum
          return sum + (Number(item.revisedSP) || 0)
        }, 0)

        // SP per Zendesk ticket per month: unique ZD count from Tickets only
        const ticketsOnly = Array.isArray(bucket.Tickets) ? bucket.Tickets : []
        const zdIds = ticketsOnly.map(t => t?.zendeskID).filter(id => id != null && id !== '')
        const uniqueZDCount = new Set(zdIds).size || 1

        bucket.AiUnits = units
        bucket.AiZDTicketCount = uniqueZDCount
        bucket.AiPerItem = units / uniqueZDCount

        return acc
      }, withJira)

      const monthKeys = Object.keys(projectKeyMonthlyData).sort()
      const monthLabels = monthKeys.map((k) => projectKeyMonthlyData[k]?.label || k)

      obj.projectMonthlyDatasets[projectKey] = {
        [catNames.BILLING]: {
          dataset: { label: projectKey, data: monthKeys.map((k) => projectKeyMonthlyData[k].Dollars), fill: false, tension: 0.1, borderColor },
          labels: monthLabels
        },
        [catNames.TRACKING]: {
          dataset: { label: projectKey, data: monthKeys.map((k) => projectKeyMonthlyData[k].SP), fill: false, tension: 0.1, borderColor },
          labels: monthLabels
        },
        [catNames.SP_MONTHLY]: {
          dataset: {
            label: projectKey,
            data: monthKeys.map((k) => {
              const m = projectKeyMonthlyData[k]
              return (m.Dollars / (m.SP > 0 ? m.SP : 1))
            }),
            fill: false,
            tension: 0.1,
            borderColor
          },
          labels: monthLabels
        },
        [catNames.TICKETS_MONTHLY]: {
          dataset: { label: projectKey, data: monthKeys.map((k) => projectKeyMonthlyData[k].TicketCount), fill: false, tension: 0.1, borderColor },
          labels: monthLabels
        },
        [catNames.AI_BILLING]: {
          dataset: {
            label: projectKey,
            data: monthKeys.map((k) =>
              sumBucketChargesForGraph(projectKeyMonthlyData[k], selectedCharges, excludedChargeRowKeys)
            ),
            fill: false,
            tension: 0.1,
            borderColor
          },
          labels: monthLabels
        },
        [catNames.AI_SP_MONTHLY]: {
          dataset: {
            label: projectKey,
            data: monthKeys.map((k) => {
              const m = projectKeyMonthlyData[k]
              const qbo = sumBucketChargesForGraph(m, selectedCharges, excludedChargeRowKeys)
              return (qbo / (m.AiUnits > 0 ? m.AiUnits : 1))
            }),
            fill: false,
            tension: 0.1,
            borderColor
          },
          labels: monthLabels
        },
        [catNames.AI_TRACKING]: {
          dataset: { label: projectKey, data: monthKeys.map((k) => projectKeyMonthlyData[k].AiUnits), fill: false, tension: 0.1, borderColor },
          labels: monthLabels
        },
        [catNames.AI_TICKET_SP]: {
          dataset: { label: projectKey, data: monthKeys.map((k) => projectKeyMonthlyData[k].AiPerItem), fill: false, tension: 0.1, borderColor },
          labels: monthLabels
        }
      }

      obj.projectMonthlyData[projectKey] = projectKeyMonthlyData
      return obj
    }, { projectMonthlyData: {}, projectMonthlyDatasets: {}, projectAvailability: {} })

    const projectMonthlyData = projectKeysReduce.projectMonthlyData
    const projectMonthlyDatasets = projectKeysReduce.projectMonthlyDatasets

    const sampleKey = Object.keys(projectMonthlyDatasets || {})[0]
    const monthLabels = projectMonthlyDatasets?.[sampleKey]?.[catNames.SP_MONTHLY]?.labels || []

    return {
      sampleKey,
      projectKeys,
      projectMonthlyData,
      projectMonthlyDatasets,
      projectAvailability: projectKeysReduce.projectAvailability,
      monthLabels
    }
  }, [
    billingInfo,
    trackerInfo,
    ticketsInfo,
    organizationTickets,
    vulnerabilityTickets,
    upgradeTickets,
    qboByCustomer,
    selectedCharges,
    excludedChargeRowKeys,
    catNames,
    graphFilters,
    clientSettings,
    clientOptions,
    projectAvailabilityOverrides
  ])
}

export { useDashboardData }
